import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { connect } from "react-redux";
import { logOut } from "../action/MainAction";
import { base_url, img_url } from "../api/index";
import { usePermissions } from "../context/PermissionsContext";
import { motion, AnimatePresence } from "motion/react";
import {
  LayoutGrid,
  ShoppingBag,
  CreditCard,
  Users,
  Bell,
  ClipboardList,
  ChefHat,
  Search,
  LogOut,
  Menu,
  Moon,
  Sun,
  Printer,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useI18n } from "../i18n/I18nContext";
import { POS_ENABLED } from "../api/pos";
import NotificationModal from "./NotificationModal";
import ConnectivityIndicator from "./ConnectivityIndicator";
import SyncIndicator from "./SyncIndicator";
import LanguageSwitcher from "./LanguageSwitcher";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  };
};

const navItems = [
  { icon: LayoutGrid, labelKey: "nav.tables", path: "/masalar" },
  { icon: ClipboardList, labelKey: "nav.orders", path: "/siparisler" },
  { icon: ChefHat, labelKey: "nav.kitchen", path: "/kitchen" },
  { icon: Users, labelKey: "nav.customers", path: "/musteriler" },
  { icon: CreditCard, labelKey: "nav.cashier", path: "/gunluk-kasa" },
  { icon: Bell, labelKey: "nav.notifications", path: "/genel-ayarlar" },
  // Yalnız yerli POS (Electron) rejimində görünür — cloud-da printer konsepti yoxdur.
  ...(POS_ENABLED
    ? [{ icon: Printer, labelKey: "nav.printers", path: "/printers" }]
    : []),
];

const Header = ({ token, logOut }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: permissionsLoading, canAccessRoute } = usePermissions();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useI18n();
  const [meData, setMeData] = useState<any>({});
    const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [formData, setFormData] = useState({
    logo: null,
    name: "",
  });

  useEffect(() => {
    localStorage.setItem("restoran_name", formData?.name);
  }, [formData]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${base_url}/own-restaurants`,
        getAuthHeaders()
      );
      setFormData({
        logo: response.data.logo || null,
        name: response.data.name || "",
      });
    } catch (error: any) {
      console.error("Error fetching settings", error);
    }
  };

  const fetchMe = async () => {
    try {
      const response = await axios.get(`${base_url}/me`, getAuthHeaders());
      setMeData(response.data);
    } catch (error: any) {
      console.error("Error fetching user data", error);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchMe();
  }, []);

  const handleLogout = async () => {
    await logOut();
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("staff_role");
    navigate("/");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    console.log("Searching for:", searchQuery);
  };

  const getInitials = () => {
    const name = meData.name || "";
    const parts = name.split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  // İcazə-əsaslı nav gating — mənbə PermissionsContext.canAccessRoute-dur
  // (permission → rol fallback → staff safe fallback ardıcıllığı orada).
  // Statik rol siyahısı burada TƏKRARLANMIR: rezmo_admin-də yaradılan custom
  // rolların icazələri əks halda nəzərə alınmırdı.
  const isNavItemAllowed = (path) => {
    if (permissionsLoading) return false;
    return canAccessRoute(path);
  };

  if (!token) {
    return null;
  }

  return (
    <>
      <header
        className={`h-[80px] flex items-center px-4 lg:px-6 z-50 shadow-sm relative border-b transition-colors duration-300 ${
          isDark ? "bg-[#1a1d27] border-[#2a2e3a]" : "bg-white border-gray-100"
        }`}
      >
        <div
          className={`absolute inset-0 pointer-events-none ${
            isDark
              ? "bg-gradient-to-r from-[#1a1d27] via-[#1e2130] to-[#1a1d27]"
              : "bg-gradient-to-r from-orange-50/50 via-white to-blue-50/30"
          }`}
        ></div>

        {/* Logo & Brand */}
        <div className="flex items-center gap-2 relative z-10">
          <img
            src={isDark ? "/rezmologodarkmode.png" : "/rezmologolightmode.png"}
            alt="Rezmo"
            className="h-11 w-auto object-contain select-none"
            draggable={false}
          />
        </div>

        {/* Navigation Items (Desktop) */}
        <nav className="hidden md:flex items-center gap-2 relative z-10 ml-8 mr-auto">
          {navItems.map((item) => {
            if (!isNavItemAllowed(item.path)) return null;

            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            const Icon = item.icon;

            return (
              <Link key={item.path} to={item.path}>
                <div
                  className={`
                    group flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 relative overflow-visible
                    ${
                      isActive
                        ? "text-white"
                        : isDark
                        ? "text-gray-400 hover:text-[var(--primary)] hover:bg-[#22252f]"
                        : "text-gray-500 hover:text-[var(--primary)] hover:bg-orange-50"
                    }
                  `}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeNavBubble"
                      className="absolute inset-0 bg-gradient-to-r from-[var(--primary)] to-[var(--warning)] rounded-full z-0 shadow-lg shadow-orange-200"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 25,
                        mass: 0.6,
                      }}
                      style={{ transformOrigin: "center" }}
                    />
                  )}

                  <div className="relative z-10">
                    <Icon
                      className={`w-5 h-5 ${isActive ? "text-white" : ""}`}
                    />
                  </div>
                  <span
                    className={`relative z-10 font-bold text-sm whitespace-nowrap ${
                      isActive ? "text-white" : ""
                    }`}
                  >
                    {t(item.labelKey)}
                  </span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-3 relative z-10">
          {/* Masalar sayfasında inline arama kutusu */}
          {location.pathname === "/masalar" && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 200, opacity: 1 }}
              className="overflow-hidden hidden md:block"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    window.dispatchEvent(new CustomEvent("masalar-search", { detail: e.target.value }));
                  }}
                  placeholder={t("header.searchTable")}
                  className={`w-full pl-9 pr-4 py-2 rounded-full text-sm outline-none transition-all ${
                    isDark
                      ? "bg-[#22252f] text-gray-200 placeholder:text-gray-500"
                      : "bg-gray-100 text-gray-700 placeholder:text-gray-500"
                  }`}
                />
              </div>
            </motion.div>
          )}

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            {/* Dil seçici */}
            <LanguageSwitcher />
            {/* İnternet əlaqəsi (cloud ping) */}
            <ConnectivityIndicator />
            {/* Sinxronlaşma vəziyyəti — yalnız POS rejimində görünür.
                ConnectivityIndicator "internet varmı" deyir, bu isə
                "məlumat gəlirmi" — ikisi fərqli suallardır: internet ola
                bilər, amma cihaz qeydiyyatdan keçməyibsə data gəlmir. */}
            <SyncIndicator />
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-xl transition-all active:scale-95 ${
                isDark
                  ? "bg-indigo-500/20 text-yellow-400 hover:bg-indigo-500/30"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
              title={isDark ? t("header.lightMode") : t("header.darkMode")}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            {/* Gelen Kutusu (garson çağrıları + müşteri siparişleri) */}
            <NotificationModal />
          </div>

          <div className={`h-8 w-px mx-1 ${isDark ? "bg-[#2a2e3a]" : "bg-gray-200"}`}></div>

          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--warning)] p-0.5 shadow-lg shadow-orange-200 cursor-pointer hover:scale-105 transition-transform">
            <div
              className={`w-full h-full rounded-full flex items-center justify-center ${
                isDark ? "bg-[#1a1d27]" : "bg-white"
              }`}
            >
              <span className="font-bold text-[var(--primary)] text-sm">
                {getInitials()}
              </span>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`p-2.5 rounded-xl transition-all active:scale-95 ${
              isDark
                ? "bg-[#22252f] text-gray-300 hover:bg-[#2a2e3a]"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <LogOut className="w-5 h-5" />
          </button>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2.5 rounded-xl transition-all ${
              isDark
                ? "bg-[#22252f] text-gray-300 hover:bg-[#2a2e3a]"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`md:hidden fixed inset-x-0 top-20 shadow-lg z-40 p-4 border-b ${
              isDark ? "bg-[#1a1d27] border-[#2a2e3a]" : "bg-white border-gray-100"
            }`}
          >
            <div className="flex flex-col gap-2">
              {navItems.map((item) => {
                if (!isNavItemAllowed(item.path)) return null;

                const isActive =
                  location.pathname === item.path ||
                  location.pathname.startsWith(item.path + "/");
                const Icon = item.icon;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? "bg-gradient-to-r from-[var(--primary)] to-[var(--warning)] text-white"
                        : isDark
                        ? "text-gray-300 hover:bg-[#22252f]"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-bold text-sm">{t(item.labelKey)}</span>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const mapStateToProps = (state) => ({
  token: state.Data.token,
});

const mapDispatchToProps = {
  logOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);


